import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Radio, Skeleton, Empty, Tag, Spin, message, Flex, Switch, Select, Checkbox } from 'antd';
import {
    ThunderboltOutlined,
    MessageOutlined,
    CheckCircleFilled,
    RocketOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    CommentOutlined,
    SendOutlined,
    StarFilled,
    FireFilled,
    PlusOutlined,
    CloseOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import callAPI from '../fetch/main';
import confetti from 'canvas-confetti';
import { trackCompletedSignup } from '../../funcs/metaPixel';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './FirstAutomationOnboarding.css';

const FirstAutomationOnboarding = ({
    open,
    onClose,
    onComplete,
    onSetupAccount,
    decodedToken
}) => {
    // Step states
    const [currentStep, setCurrentStep] = useState(0);

    // Posts loading
    const [loading, setLoading] = useState(false);
    const [posts, setPosts] = useState([]);
    const [noPosts, setNoPosts] = useState(false); // Track if user has no posts

    // User selections
    const [selectedPost, setSelectedPost] = useState(null);
    const [triggerType, setTriggerType] = useState('all'); // 'all' or 'specific'
    const [triggerWords, setTriggerWords] = useState([]); // Array of trigger words
    const [triggerInput, setTriggerInput] = useState(''); // Input for adding new words
    const [dmMessage, setDmMessage] = useState('');

    // Automation creation
    const [creating, setCreating] = useState(false);
    const [automationCreated, setAutomationCreated] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Comment tracking for test screen
    const [testComments, setTestComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [initialCommentIds, setInitialCommentIds] = useState(null); // Track first poll
    const [pollingStartTime, setPollingStartTime] = useState(null);
    const [newSuccessDetected, setNewSuccessDetected] = useState(false);
    const [pollingStopped, setPollingStopped] = useState(false);

    // Email/OTP verification states
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpStep, setOtpStep] = useState('email'); // 'email' or 'verify'
    const [otpSending, setOtpSending] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);

    // Onboarding questions states
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [onboardingData, setOnboardingData] = useState({
        previousTools: [],
        businessType: [],
        mainGoal: [],
        website: ''
    });

    // Quick Automation Templates - User-friendly templates for common use cases
    const quickAutomationTemplates = [
        {
            id: 'send-link',
            label: 'Send Just a Link',
            icon: '🔗',
            description: 'Share a URL with your audience',
            color: '#3b82f6',
            fields: ['link'],
            defaultMessage: (config) => `Hey! 👋 Thanks for commenting! Here's the link:\n🔗 ${config.link || '[Your Link]'}\nLet me know if you have any questions!`,
            triggerWords: ['link', 'send', 'dm', 'want']
        },
        {
            id: 'send-price',
            label: 'Send Price Information',
            icon: '💰',
            description: 'Share pricing details automatically',
            color: '#10b981',
            fields: ['price', 'currency', 'productName'],
            defaultMessage: (config) => `Hey! 👋 Thanks for your interest!\n💰 Price: ${config.currency || '$'}${config.price || '0'}${config.productName ? ` for ${config.productName}` : ''}\n🛒 Get it here: ${config.link || '[Your Link]'}\nLet me know if you have any questions!`,
            triggerWords: ['price', 'cost', 'pp', 'how much', 'rate']
        },
        // {
        //     id: 'send-button',
        //     label: 'Send a Button Template',
        //     icon: '🔘',
        //     description: 'Interactive message with clickable buttons',
        //     color: '#8b5cf6',
        //     fields: ['title', 'description', 'buttonText', 'buttonUrl'],
        //     defaultMessage: (config) => `${config.title || 'Check this out!'}\n${config.description || 'Click below to learn more.'}`,
        //     triggerWords: ['interested', 'info', 'details', 'more']
        // },
        {
            id: 'send-location',
            label: 'Send Address/Location',
            icon: '📍',
            description: 'Share your store or business location',
            color: '#f59e0b',
            fields: ['address', 'mapsLink', 'timings', 'phone'],
            defaultMessage: (config) => `Hey! 🏪 Here are our details:\n📍 Location: ${config.address || '[Your Address]'}\n🕐 Timings: ${config.timings || 'Mon-Sat, 10AM - 8PM'}\n📱 Contact: ${config.phone || '[Your Number]'}\nSee you soon! 🙌`,
            triggerWords: ['location', 'address', 'where', 'store', 'visit', 'directions']
        },
        // {
        //     id: 'send-product',
        //     label: 'Send Product Information',
        //     icon: '🛍️',
        //     description: 'Share product details with image, title & link',
        //     color: '#ec4899',
        //     fields: ['productImage', 'productTitle', 'productDescription', 'productLink'],
        //     defaultMessage: (config) => `Hey! 🛍️ Here's the product you asked about:\n✨ ${config.productTitle || '[Product Name]'}\n📝 ${config.productDescription || '[Product Description]'}\n🔗 Get it here: ${config.productLink || '[Product Link]'}`,
        //     triggerWords: ['product', 'buy', 'shop', 'order', 'get'],
        //     hidden: true // Hidden from onboarding
        // }
    ];

    // Template configuration state
    const [templateConfig, setTemplateConfig] = useState({
        link: '',
        price: '',
        currency: '$',
        productName: '',
        title: '',
        description: '',
        buttonText: 'Learn More',
        buttonUrl: '',
        address: '',
        mapsLink: '',
        timings: '',
        phone: '',
        productImage: '',
        productTitle: '',
        productDescription: '',
        productLink: ''
    });

    // Followers only settings
    const [followersOnly, setFollowersOnly] = useState(false);
    const [followersOnlyMessage, setFollowersOnlyMessage] = useState("Hey! 👋 Follow me first to get the exclusive content!");

    // Legacy category templates for backward compatibility (keeping for niche selection)
    const categoryTemplates = [
        {
            id: 'coaches',
            label: 'Coaches & Educators',
            icon: '🎓',
            emoji: '📚',
            description: 'Perfect for course creators, mentors & coaches',
            triggerWords: ['join', 'pdf', 'growth', 'interested', 'ebook', 'course', 'enroll', 'learn', 'coaching', 'guide', 'free', 'book'],
            dmMessage: "Hey! 👋\n\nSo excited you're interested in leveling up! 🚀\n\nHere's the exclusive resource I promised:\n\n📚 [Your Link Here]\n\nThis will help you with your growth journey. If you have any questions, feel free to reply!\n\nLet's grow together! 💪",
            color: '#8b5cf6'
        },
        {
            id: 'ecommerce',
            label: 'Shopify & E-commerce',
            icon: '🛍️',
            emoji: '💳',
            description: 'For online stores & product sellers',
            triggerWords: ['price', 'link', 'purchase', 'buy', 'website', 'shop', 'order', 'deal', 'discount', 'sale', 'cost', 'pp', 'available', 'stock'],
            dmMessage: "Hey! 🛍️\n\nThanks for your interest in our product!\n\n🔗 Shop here: [Your Link]\n\n💳 Use code SAVE10 for 10% off your first order!\n\nLet me know if you have any questions about sizing, shipping, or anything else!",
            color: '#f59e0b'
        },
        {
            id: 'brands',
            label: 'Brands & Businesses',
            icon: '🏢',
            emoji: '✨',
            description: 'For established brands & companies',
            triggerWords: ['info', 'details', 'more', 'how', 'services', 'collab', 'partner', 'work', 'pricing', 'contact', 'quote', 'booking', 'appointment'],
            dmMessage: "Hey! ✨\n\nThank you for reaching out!\n\nWe'd love to share more about what we do:\n\n🔗 Learn more: [Your Link]\n📅 Book a call: [Calendar Link]\n\nFeel free to reply with any specific questions. We're here to help!",
            color: '#3b82f6'
        },
        {
            id: 'influencers',
            label: 'Content Creators',
            icon: '⭐',
            emoji: '🔗',
            description: 'For influencers & content creators',
            triggerWords: ['link', 'pdf', 'app', 'resource', 'tool', 'send', 'share', 'dm', 'recommendation', 'favorite', 'routine', 'list', 'tips'],
            dmMessage: "Hey! ⭐\n\nThanks for being part of my community! 💜\n\nHere's what I promised:\n\n🔗 Link: [Your Link]\n\nLet me know if you try it out - I'd love to hear your thoughts!\n\nMuch love! 💕",
            color: '#ec4899'
        },
        {
            id: 'ugc',
            label: 'UGC & Affiliates',
            icon: '💄',
            emoji: '🛒',
            description: 'For UGC creators & affiliate marketers',
            triggerWords: ['link', 'product', 'skin', 'lipstick', 'dress', 'buy', 'pp', 'app', 'code', 'haul', 'outfit', 'makeup', 'skincare', 'review', 'wear', 'shop'],
            dmMessage: "Hey! 💄\n\nOMG thanks for asking about the product!\n\n🛒 Get it here: [Affiliate Link]\n\n💰 Use my code: [YOUR CODE] for a special discount!\n\nLet me know what you think once you try it! 💕",
            color: '#f43f5e'
        },
        {
            id: 'offline',
            label: 'Offline / Local Stores',
            icon: '🏪',
            emoji: '📍',
            description: 'For physical stores & local businesses',
            triggerWords: ['store', 'location', 'address', 'place', 'whatsapp', 'number', 'phone', 'visit', 'shop', 'open', 'timing', 'hours', 'near', 'directions'],
            dmMessage: "Hey! 🏪\n\nThanks for reaching out! Here are our store details:\n\n📍 Location: [Your Store Address]\n\n🕐 Timings: [Mon-Sat, 10AM - 8PM]\n\n📱 WhatsApp: [Your Number]\n\nFeel free to visit us or ping on WhatsApp for any queries! See you soon! 🙌",
            color: '#10b981'
        },
        {
            id: 'others',
            label: 'Others',
            icon: '✨',
            emoji: '🔗',
            description: 'General purpose automation',
            triggerWords: ['link', 'pp', 'price', 'pdf', 'growth'],
            dmMessage: "Hey! 👋\n\nThanks for reaching out!\n\nHere's what you asked for:\n\n🔗 [Your Link Here]\n\nLet me know if you have any questions!",
            color: '#64748b'
        }
    ];

    // Selected quick template
    const [selectedQuickTemplate, setSelectedQuickTemplate] = useState(null);

    // Simple message templates for custom option
    const messageTemplates = [
        {
            label: "Share a Link",
            value: "Hey! 👋\n\nThanks for your interest! Here's the link you requested:\n\n🔗 [Your Link Here]\n\nLet me know if you have any questions!",
            icon: "🔗"
        },
        {
            label: "Send Discount Code",
            value: "Hey! 🎉\n\nThanks for commenting! Here's your exclusive discount:\n\n💳 Code: SAVE20\n\nUse it at checkout to save 20%! Link: [Your Link]",
            icon: "🎁"
        },
        {
            label: "Lead Magnet / Freebie",
            value: "Hey! ✨\n\nExcited that you want the freebie! Here it is:\n\n📥 Download: [Your Link]\n\nEnjoy and let me know what you think!",
            icon: "📥"
        },
        {
            label: "Thank You Message",
            value: "Hey! 💜\n\nThank you so much for engaging with my content! It means a lot.\n\nFeel free to DM me anytime if you have questions!",
            icon: "💜"
        },
    ];

    // Fetch posts when modal opens
    useEffect(() => {
        if (open && posts.length === 0) {
            fetchPosts();
        }
    }, [open]);

    // Poll for comments when on Step 3 (Test screen)
    useEffect(() => {
        let intervalId;
        let timeoutId;

        if (currentStep === 3 && selectedPost?.id && !pollingStopped && !newSuccessDetected) {
            // Set polling start time on first entry
            if (!pollingStartTime) {
                setPollingStartTime(Date.now());
            }

            // Fetch immediately
            fetchComments();

            // Calculate elapsed time in minutes
            const getElapsedMinutes = () => {
                if (!pollingStartTime) return 0;
                return (Date.now() - pollingStartTime) / 60000;
            };

            // Determine polling interval based on elapsed time
            const getPollingInterval = () => {
                const elapsed = getElapsedMinutes();
                if (elapsed >= 10) {
                    return 45000; // 45 seconds after 10 mins
                }
                return 15000; // 15 seconds for first 10 mins
            };

            // Start polling
            const startPolling = () => {
                const interval = getPollingInterval();
                intervalId = setInterval(() => {
                    const elapsed = getElapsedMinutes();

                    // Stop polling after 20 minutes
                    if (elapsed >= 20) {
                        setPollingStopped(true);
                        clearInterval(intervalId);
                        return;
                    }

                    fetchComments();
                }, interval);
            };

            startPolling();

            // Check if we need to switch to slower polling after 10 mins
            const remaining = 10 * 60000 - (Date.now() - (pollingStartTime || Date.now()));
            if (remaining > 0) {
                timeoutId = setTimeout(() => {
                    clearInterval(intervalId);
                    startPolling(); // Restart with new interval
                }, remaining);
            }
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [currentStep, selectedPost?.id, pollingStopped, newSuccessDetected, pollingStartTime]);

    // Reset polling states when leaving Step 3
    useEffect(() => {
        if (currentStep !== 3) {
            setInitialCommentIds(null);
            setPollingStartTime(null);
            setNewSuccessDetected(false);
            setPollingStopped(false);
        }
    }, [currentStep]);

    // Trigger confetti when reaching Step 6 (You're All Set)
    useEffect(() => {
        if (currentStep === 6) {
            confetti({
                particleCount: 150,
                spread: 80,
                origin: { y: 0.6 },
                colors: ['#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#10b981'],
                zIndex: 10000
            });
        }
    }, [currentStep]);

    const fetchPosts = () => {
        setLoading(true);
        callAPI({
            url: 'ig-posts',
            method: 'GET',
            loadingState: setLoading,
            dataState: (response) => {
                if (response?.posts_lists?.data) {
                    const postsData = response.posts_lists.data.slice(0, 12);
                    setPosts(postsData);
                    if (postsData.length === 0) {
                        setNoPosts(true);
                    }
                } else {
                    setNoPosts(true);
                }
            }
        });
    };

    const fetchComments = () => {
        if (!selectedPost?.id) return;

        callAPI({
            url: `ig-post-comments?post_id=${selectedPost.id}`,
            method: 'GET',
            loadingState: setLoadingComments,
            dataState: (response) => {
                if (response?.code === 200 && response?.data?.items) {
                    const items = response.data.items;

                    // First poll - store initial comment IDs
                    if (initialCommentIds === null) {
                        const ids = new Set(items.map(c => c.comment_id));
                        setInitialCommentIds(ids);
                        setTestComments(items);
                        return;
                    }

                    // Check for new comments that have been responded to
                    const newSuccessfulComments = items.filter(comment =>
                        !initialCommentIds.has(comment.comment_id) && comment.responded
                    );

                    if (newSuccessfulComments.length > 0 && !newSuccessDetected) {
                        setNewSuccessDetected(true);

                        // Trigger confetti celebration
                        confetti({
                            particleCount: 100,
                            spread: 70,
                            origin: { y: 0.6 },
                            colors: ['#10b981', '#6366f1', '#8b5cf6', '#f59e0b']
                        });

                        message.success('🎉 It worked! Your automation is working perfectly!');
                    }

                    setTestComments(items);
                }
            }
        });
    };

    const handleNext = () => {
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    // OTP Timer countdown
    useEffect(() => {
        let interval;
        if (otpTimer > 0) {
            interval = setInterval(() => {
                setOtpTimer((timer) => timer - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [otpTimer]);

    // Send OTP to email
    const handleSendOtp = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            message.error('Please enter a valid email address');
            return;
        }

        setOtpSending(true);
        message.loading({ content: 'Sending OTP to your email...', key: 'otp-send' });

        callAPI({
            url: `register_otp_email?email_id=${email}`,
            method: 'PUT',
            body: {},
            loadingState: setOtpSending,
            dataState: (data) => {
                message.destroy('otp-send');
                if (data?.code === 200) {
                    message.success('OTP sent to your email!');
                    setOtpStep('verify');
                    setOtpTimer(60);
                    setOtp('');
                } else {
                    message.error(data?.message || 'Failed to send OTP. Please try again.');
                }
            }
        });
    };

    // Verify OTP
    const handleVerifyOtp = () => {
        if (!otp || otp.length !== 6) {
            message.error('Please enter the 6-digit OTP');
            return;
        }

        setOtpVerifying(true);
        message.loading({ content: 'Verifying OTP...', key: 'otp-verify' });

        callAPI({
            url: 'register_otp_verify',
            method: 'PUT',
            body: { otp, email_id: email },
            loadingState: () => { }, // Dummy function since we're managing loading manually
            dataState: (data) => {
                message.destroy('otp-verify');
                if (data?.code === 200) {
                    // After OTP verification, update the email
                    message.loading({ content: 'Updating email...', key: 'update-email' });

                    callAPI({
                        url: 'update_email',
                        method: 'PUT',
                        body: { emailid: email },
                        loadingState: () => { }, // Dummy function
                        dataState: (updateData) => {
                            message.destroy('update-email');

                            if (updateData?.code == "200") {
                                // Track Meta Pixel: User completed signup (finished onboarding with email)
                                trackCompletedSignup({
                                    email: email,
                                    external_id: decodedToken?.user_id,
                                    firstName: decodedToken?.name?.split(' ')[0]
                                });

                                // Update token in localStorage
                                localStorage.setItem("token", updateData.token);
                                sessionStorage.clear();
                                localStorage.removeItem("refCode");

                                message.success('Email verified successfully!');
                                setOtpVerifying(false);

                                // Proceed to onboarding questions step
                                setCurrentStep(5);

                                // Delay to allow state to update
                                setTimeout(() => {
                                    localStorage.setItem("token", updateData.token);
                                    sessionStorage.clear();
                                    localStorage.removeItem("refCode");
                                }, 1200);
                            } else {
                                message.error(updateData?.message || 'Failed to update email. Please try again.');
                                setOtpVerifying(false);
                            }
                        }
                    });
                } else {
                    message.error(data?.message || 'Invalid OTP. Please try again.');
                    setOtpVerifying(false);
                }
            }
        });
    };

    // Resend OTP
    const handleResendOtp = () => {
        if (otpTimer > 0) return;
        handleSendOtp();
    };

    const handleCreateAutomation = () => {
        if (!selectedPost || !dmMessage.trim()) {
            message.error('Please configure your automation properly');
            return;
        }

        setCreating(true);

        // Prepare trigger words
        let triggers = [];
        let automateAll = "false";

        if (triggerType === 'all') {
            triggers = ['*allwordautomation*'];
            automateAll = "true";
        } else {
            triggers = triggerWords.filter(w => w.trim());
            if (triggers.length === 0) {
                message.error('Please enter at least one trigger word');
                setCreating(false);
                return;
            }
        }

        // Get current timestamp
        const now = new Date();
        const automated_at = now.toISOString().replace('T', ' ').slice(0, 26);

        // Create automation payload using the correct template
        const default_automation_temp = {
            sp_id: null,
            user_id: decodedToken?.user_id,
            global_automation: false,
            auto_reply: {
                buttons: [
                    {
                        title: "Learn More",
                        type: "web_url",
                        url: "https://instantdm.com"
                    }
                ],
                automate_all: automateAll,
                triger_words: triggers,
                welcome_message: {
                    enabled: false,
                    text: "Thanks for commenting",
                    button_text: "Get your link"
                },
                dms: [dmMessage],
                title: "Automated via Onboarding",
                enable_comment_reply: true,
                products: [],
                dm_template_type: "text",
                dm_reply_type: "single",
                delay: "0",
                include_ai_comment_reply: true,
                reply_comments: [
                    "Sent ✅",
                    "Check DM 📧",
                    "DMed you!"
                ],
                enable: true,
                subtitle: "",
                messages: [],
                cover_image: "",
                enable_dm_reply: true
            },
            post_id: selectedPost.id,
            shopify_linked: false,
            auto_hide: {
                enable: false,
                triger_words: [],
                ai_analysis: []
            },
            automated_at: automated_at,
            flow_editor: false,
            auto_delete: {
                enable: false,
                triger_words: [],
                ai_analysis: []
            }
        };

        callAPI({
            url: 'automate-post?demo=true',
            method: 'POST',
            body: default_automation_temp,
            loadingState: setCreating,
            dataState: (response) => {
                if (response?.code === 200) {
                    setAutomationCreated(true);
                    setCurrentStep(3); // Go to test step

                    message.success('🎉 Your first automation is live!');
                } else {
                    message.error(response?.message || 'Failed to create automation');
                }
            }
        });
    };

    const renderStepIndicator = () => (
        <div className="fao-step-indicator">
            {[0, 1, 2, 3, 4, 5].map((step) => (
                <div
                    key={step}
                    className={`fao-step-dot ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                />
            ))}
        </div>
    );

    const renderStep0 = () => (
        <div className="fao-step fao-step-welcome">
            {/* User Profile */}
            <div className="fao-welcome-profile">
                <img
                    src={decodedToken?.profile_picture_url || '/logo192.png'}
                    alt="Profile"
                    className="fao-welcome-avatar"
                />
            </div>
            <h2 className="fao-title">Welcome to InstantDM! 👋</h2>
            <p className="fao-welcome-username">@{decodedToken?.username || 'your_account'}</p>
            <p className="fao-subtitle">
                Let's automate your first post in just 60 seconds!
            </p>

            <div className="fao-benefits">
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Boost Sales & Engagement</span>
                </div>
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Save Time on DMs</span>
                </div>
                <div className="fao-benefit-item">
                    <CheckCircleFilled className="fao-check-icon" />
                    <span>Works 24/7 Automatically</span>
                </div>
            </div>

            {noPosts ? (
                <div className="fao-no-posts-cta">
                    <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                        📭 No posts found on your account yet. Let's set up your account first!
                    </p>
                    <Button
                        type="primary"
                        size="large"
                        className="fao-start-btn"
                        icon={<RocketOutlined />}
                        onClick={() => {
                            onComplete?.();
                            onSetupAccount?.();
                            onClose?.();
                        }}
                    >
                        Setup your Account
                    </Button>
                </div>
            ) : (
                <div className="fao-welcome-ctas">
                    <Button
                        type="primary"
                        size="large"
                        className="fao-start-btn"
                        icon={<ArrowRightOutlined />}
                        onClick={handleNext}
                    >
                        Automate my first post
                    </Button>
                    <Button
                        type="text"
                        className="fao-skip-link"
                        onClick={() => {
                            // Skip automation setup and go directly to email verification
                            setCurrentStep(4);
                        }}
                    >
                        I'll do it later, continue with account setup →
                    </Button>
                </div>
            )}
        </div>
    );

    const renderStep1 = () => (
        <div className="fao-step fao-step-posts">
            <h2 className="fao-title">
                <span className="fao-step-number">1</span>
                Select a Post/Reel to Automate
            </h2>
            <p className="fao-subtitle">Choose the post you want to set up automation for</p>

            {loading ? (
                <div className="fao-posts-loading">
                    {Array(6).fill(0).map((_, idx) => (
                        <Skeleton.Image
                            key={idx}
                            style={{ width: 100, height: 100, borderRadius: 12 }}
                            active
                        />
                    ))}
                </div>
            ) : posts.length === 0 ? (
                <Empty
                    description="No posts found. Please make sure you have posts on your Instagram."
                    style={{ margin: '40px 0' }}
                />
            ) : (
                <div className="fao-posts-grid">
                    {posts.map((post) => (
                        <div
                            key={post.id}
                            className={`fao-post-item ${selectedPost?.id === post.id ? 'selected' : ''}`}
                            onClick={() => setSelectedPost(post)}
                        >
                            <img
                                src={post.thumbnail_url || post.media_url || post.full_picture}
                                alt="Post"
                                onError={(e) => {
                                    e.target.src = '/placeholder-post.png';
                                }}
                            />
                            {selectedPost?.id === post.id && (
                                <div className="fao-post-selected-overlay">
                                    <CheckCircleFilled />
                                </div>
                            )}
                            {post.media_type === 'VIDEO' && (
                                <div className="fao-post-video-badge">▶ REEL</div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const handleCategorySelect = (category) => {
        setSelectedCategory(category.id);
        setTriggerType('specific');
        setTriggerWords([...category.triggerWords]); // Use array
        // Don't prefill DM message - let user add their own
    };

    // Add a new trigger word
    const addTriggerWord = () => {
        const word = triggerInput.trim().toLowerCase();
        if (word && !triggerWords.includes(word)) {
            setTriggerWords([...triggerWords, word]);
            setTriggerInput('');
        } else if (triggerWords.includes(word)) {
            message.warning('This word is already added');
            setTriggerInput('');
        }
    };

    // Remove a trigger word
    const removeTriggerWord = (wordToRemove) => {
        setTriggerWords(triggerWords.filter(word => word !== wordToRemove));
    };

    // Handle Enter key in input
    const handleTriggerInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addTriggerWord();
        }
    };

    const renderStep2 = () => {
        // Get the selected template object
        const selectedTemplate = quickAutomationTemplates.find(t => t.id === selectedQuickTemplate);

        // Generate preview message based on selected template and config
        const getPreviewMessage = () => {
            if (!selectedTemplate) return 'Select a template to see preview';
            return selectedTemplate.defaultMessage(templateConfig);
        };

        // Render config fields based on template
        const renderConfigFields = () => {
            if (!selectedTemplate) return null;

            switch (selectedQuickTemplate) {
                case 'send-link':
                    return (
                        <div className="fao-config-fields">
                            <Input.TextArea
                                placeholder="Type your message here... Include any links you want to share."
                                value={dmMessage}
                                onChange={(e) => setDmMessage(e.target.value)}
                                rows={4}
                                className="fao-message-textarea"
                            />
                        </div>
                    );
                case 'send-price':
                    return (
                        <div className="fao-config-fields">
                            <Input.TextArea
                                placeholder="Type your pricing message here... Include product name, price, and any links."
                                value={dmMessage}
                                onChange={(e) => setDmMessage(e.target.value)}
                                rows={4}
                                className="fao-message-textarea"
                            />
                        </div>
                    );
                // case 'send-button':
                //     return (
                //         <div className="fao-config-fields">
                //             <Input
                //                 placeholder="Message Title"
                //                 value={templateConfig.title}
                //                 onChange={(e) => {
                //                     const newConfig = { ...templateConfig, title: e.target.value };
                //                     setTemplateConfig(newConfig);
                //                     setDmMessage(selectedTemplate.defaultMessage(newConfig));
                //                 }}
                //                 prefix={<span>📝</span>}
                //             />
                //             <Input.TextArea
                //                 placeholder="Description"
                //                 value={templateConfig.description}
                //                 onChange={(e) => {
                //                     const newConfig = { ...templateConfig, description: e.target.value };
                //                     setTemplateConfig(newConfig);
                //                     setDmMessage(selectedTemplate.defaultMessage(newConfig));
                //                 }}
                //                 rows={2}
                //             />
                //             <div className="fao-config-row">
                //                 <Input
                //                     placeholder="Button Text"
                //                     value={templateConfig.buttonText}
                //                     onChange={(e) => setTemplateConfig({ ...templateConfig, buttonText: e.target.value })}
                //                     style={{ width: '40%' }}
                //                 />
                //                 <Input
                //                     placeholder="Button URL"
                //                     value={templateConfig.buttonUrl}
                //                     onChange={(e) => setTemplateConfig({ ...templateConfig, buttonUrl: e.target.value })}
                //                     prefix={<span>🔗</span>}
                //                     style={{ flex: 1 }}
                //                 />
                //             </div>
                //         </div>
                //     );
                case 'send-location':
                    return (
                        <div className="fao-config-fields">
                            <Input.TextArea
                                placeholder="Type your location/address message here... Include address, timings, contact info, and maps link."
                                value={dmMessage}
                                onChange={(e) => setDmMessage(e.target.value)}
                                rows={4}
                                className="fao-message-textarea"
                            />
                        </div>
                    );
                // case 'send-product':
                //     return (
                //         <div className="fao-config-fields">
                //             <Input.TextArea
                //                 placeholder="Type your product message here... Include product name, description, price, and link."
                //                 value={dmMessage}
                //                 onChange={(e) => setDmMessage(e.target.value)}
                //                 rows={4}
                //                 className="fao-message-textarea"
                //             />
                //         </div>
                //     );
                default:
                    return null;
            }
        };

        return (
            <div className="fao-step fao-step-config">
                <h2 className="fao-title">
                    <span className="fao-step-number">2</span>
                    Quick Automation Setup
                </h2>
                <p className="fao-subtitle">Pick a template and customize your automated DM</p>

                {/* Quick Template Selection */}
                <div className="fao-config-section">
                    <h4><span className="fao-section-step">Step 1</span> 📦 Pick a Template</h4>
                    <div className="fao-quick-template-grid">
                        {quickAutomationTemplates.filter(t => !t.hidden).map((template) => (
                            <div
                                key={template.id}
                                className={`fao-quick-template-card ${selectedQuickTemplate === template.id ? 'selected' : ''}`}
                                onClick={() => {
                                    setSelectedQuickTemplate(template.id);
                                    setTriggerWords([...template.triggerWords]);
                                    setTriggerType('specific');
                                    setDmMessage(template.defaultMessage(templateConfig));
                                }}
                                style={{
                                    '--template-color': template.color,
                                    borderColor: selectedQuickTemplate === template.id ? template.color : undefined
                                }}
                            >
                                <div className="fao-quick-template-icon">{template.icon}</div>
                                <div className="fao-quick-template-info">
                                    <div className="fao-quick-template-label">{template.label}</div>
                                    <div className="fao-quick-template-desc">{template.description}</div>
                                </div>
                                {selectedQuickTemplate === template.id && (
                                    <CheckCircleFilled className="fao-template-check" style={{ color: template.color }} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Configuration Fields - Show only when template is selected */}
                {selectedQuickTemplate && (
                    <div className="fao-config-section">
                        <h4><span className="fao-section-step">Step 2</span> {selectedTemplate?.icon} Customize Your Message</h4>
                        {renderConfigFields()}
                    </div>
                )}

                {/* Live Preview - Instagram Style DM - HIDDEN FOR NOW */}
                {/* {selectedQuickTemplate && (
                    <div className="fao-config-section">
                        <h4>📱 DM Preview</h4>
                        <div className="fao-ig-dm-preview">
                            <div className="fao-ig-dm-header">
                                <img
                                    src={decodedToken?.profile_picture_url || '/logo192.png'}
                                    alt="Profile"
                                    className="fao-ig-dm-avatar"
                                />
                                <span className="fao-ig-dm-username">@{decodedToken?.username || 'your_account'}</span>
                            </div>
                            <div className="fao-ig-dm-messages">
                                <div className="fao-ig-dm-message sent">
                                    {getPreviewMessage().split('\n').map((line, idx) => (
                                        <span key={idx}>{line}{idx < getPreviewMessage().split('\n').length - 1 && <br />}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )} */}

                {/* Trigger Words Section */}
                <div className="fao-config-section">
                    <h4><span className="fao-section-step">Step 3</span> <CommentOutlined /> Trigger Comments</h4>
                    <div className="fao-trigger-type-row">
                        <Radio.Group
                            value={triggerType}
                            onChange={(e) => setTriggerType(e.target.value)}
                            size="small"
                            buttonStyle="solid"
                        >
                            <Radio.Button value="all">
                                <FireFilled style={{ color: triggerType === 'all' ? '#fff' : '#f59e0b', marginRight: 4 }} />
                                All Comments
                            </Radio.Button>
                            <Radio.Button value="specific">
                                <StarFilled style={{ color: triggerType === 'specific' ? '#fff' : '#8b5cf6', marginRight: 4 }} />
                                Specific Keywords
                            </Radio.Button>
                        </Radio.Group>
                    </div>

                    {triggerType === 'specific' && (
                        <div className="fao-trigger-words-input">
                            <div className="fao-trigger-input-wrapper">
                                <Input
                                    placeholder="Type a keyword and press Enter"
                                    value={triggerInput}
                                    onChange={(e) => setTriggerInput(e.target.value)}
                                    onKeyDown={handleTriggerInputKeyDown}
                                    prefix={<MessageOutlined />}
                                    suffix={
                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={addTriggerWord}
                                            disabled={!triggerInput.trim()}
                                            className="fao-add-word-btn"
                                        />
                                    }
                                />
                            </div>
                            {triggerWords.length > 0 && (
                                <div className="fao-trigger-tags">
                                    {triggerWords.map((word, idx) => (
                                        <Tag
                                            key={idx}
                                            color="blue"
                                            closable
                                            onClose={() => removeTriggerWord(word)}
                                            className="fao-trigger-tag"
                                        >
                                            {word}
                                        </Tag>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Followers Only Option - HIDDEN FOR NOW */}
                {/* <div className="fao-config-section">
                    <div className="fao-followers-toggle">
                        <div className="fao-toggle-info">
                            <h4 style={{ margin: 0 }}>👥 Followers Only</h4>
                            <small>Only send DMs to your followers</small>
                        </div>
                        <Switch
                            checked={followersOnly}
                            onChange={(checked) => setFollowersOnly(checked)}
                        />
                    </div>
                    {followersOnly && (
                        <Input.TextArea
                            placeholder="Message for non-followers..."
                            value={followersOnlyMessage}
                            onChange={(e) => setFollowersOnlyMessage(e.target.value)}
                            rows={2}
                            style={{ marginTop: 12 }}
                        />
                    )}
                </div> */}
            </div>
        );
    };

    const renderStep3 = () => (
        <div className="fao-step fao-step-test">
            <h2 className="fao-title">
                <span className="fao-step-number">3</span>
                Test Your Automation 🧪
            </h2>
            <p className="fao-subtitle">
                Your automation is now live! Let's test it to make sure everything works perfectly.
            </p>

            {/* Post Preview */}
            {selectedPost && (
                <div className="fao-test-post-preview">
                    <img
                        src={selectedPost.thumbnail_url || selectedPost.media_url || selectedPost.full_picture}
                        alt="Your post"
                        className="fao-test-post-image"
                    />
                </div>
            )}

            {/* Trigger Words Display */}
            <div className="fao-test-triggers">
                <h4>Comment one of these words:</h4>
                <div className="fao-test-trigger-tags">
                    {triggerType === 'all' ? (
                        <Tag color="green" className="fao-test-tag">Any comment will trigger</Tag>
                    ) : (
                        triggerWords.slice(0, 5).map((word, idx) => (
                            <Tag key={idx} color="blue" className="fao-test-tag">{word}</Tag>
                        ))
                    )}
                </div>
            </div>

            {/* Comment Tracking Section */}
            <div className={`fao-comment-tracker ${newSuccessDetected ? 'success' : ''}`}>
                <div className="fao-comment-tracker-header">
                    {newSuccessDetected ? (
                        <h4>✅ Automation Working!</h4>
                    ) : pollingStopped ? (
                        <h4>⏹️ Tracking Stopped</h4>
                    ) : (
                        <>
                            <h4>📡 Live Comment Tracking</h4>
                            {loadingComments && <Spin size="small" />}
                        </>
                    )}
                </div>

                {newSuccessDetected ? (
                    <div className="fao-success-message">
                        <CheckCircleFilled style={{ fontSize: 32, color: '#10b981' }} />
                        <p>Your automation is working perfectly!</p>
                        <small>New comments are being responded to automatically</small>
                    </div>
                ) : testComments.length === 0 ? (
                    <div className="fao-no-comments">
                        <div className="fao-waiting-animation">⏳</div>
                        <p>Waiting for comments...</p>
                        <small>Comment on your post from another account to test</small>
                    </div>
                ) : (
                    <div className="fao-comments-list">
                        {testComments.slice(0, 5).map((comment, idx) => (
                            <div key={comment.comment_id || idx} className={`fao-comment-item ${comment.reason ? 'has-reason' : ''}`}>
                                <div className="fao-comment-info">
                                    <span className="fao-comment-username">@{comment.event_core?.from?.username || 'user'}</span>
                                    <span className="fao-comment-text">"{comment.event_core?.text || 'Comment'}"</span>
                                </div>
                                <div className={`fao-comment-status ${comment.responded ? 'success' : comment.reason ? 'error' : 'waiting'}`}>
                                    {comment.responded ? (
                                        <>
                                            <CheckCircleFilled style={{ color: '#10b981' }} />
                                            <span>DM Sent!</span>
                                        </>
                                    ) : comment.reason ? (
                                        <>
                                            <span className="fao-comment-reason">⚠️ {comment.reason}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Spin size="small" />
                                            <span>Sending...</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Instructions */}
            <div className="fao-test-instructions">
                <div className="fao-test-step">
                    <div className="fao-test-step-number">1</div>
                    <div className="fao-test-step-text">
                        <strong>Open Instagram</strong>
                        <span>Use your personal or second account</span>
                    </div>
                </div>
                <div className="fao-test-step">
                    <div className="fao-test-step-number">2</div>
                    <div className="fao-test-step-text">
                        <strong>Find your post</strong>
                        <span>Go to the post you just automated</span>
                    </div>
                </div>
                <div className="fao-test-step">
                    <div className="fao-test-step-number">3</div>
                    <div className="fao-test-step-text">
                        <strong>Comment a trigger word</strong>
                        <span>You'll receive a DM within seconds! ✨</span>
                    </div>
                </div>
            </div>

            <Button
                type="primary"
                size="large"
                className="fao-test-done-btn"
                onClick={() => setCurrentStep(4)}
            >
                {testComments.some(c => c.responded) ? "It's Working! Continue →" : "I've Tested It! Continue →"}
            </Button>
        </div>
    );

    const renderStep4 = () => (
        <div className="fao-step fao-step-email">
            {automationCreated && (
                <div className="fao-email-success-banner">
                    <CheckCircleFilled style={{ fontSize: 28, color: '#10b981' }} />
                    <div>
                        <strong>🎉 Automation Created Successfully!</strong>
                        <p>Your automation is now live and working</p>
                    </div>
                </div>
            )}

            <h2 className="fao-title">
                {automationCreated && <span className="fao-step-number">4</span>}
                Verify Your Email 📧
            </h2>
            <p className="fao-subtitle">
                Enter your email to receive important updates and secure your account
            </p>

            <div className="fao-email-form">
                {otpStep === 'email' ? (
                    <>
                        <div className="fao-email-input-group">
                            <Input
                                size="large"
                                placeholder="Enter your email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onPressEnter={handleSendOtp}
                                prefix={<span>📧</span>}
                                className="fao-email-input"
                            />
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleSendOtp}
                                loading={otpSending}
                                className="fao-send-otp-btn"
                            >
                                Send OTP
                            </Button>
                        </div>
                        <p className="fao-email-hint">
                            We'll send a 6-digit code to verify your email
                        </p>
                    </>
                ) : (
                    <>
                        <div className="fao-otp-sent-info">
                            <CheckCircleFilled style={{ color: '#10b981' }} />
                            <span>OTP sent to <strong>{email}</strong></span>
                            <Button
                                type="link"
                                size="small"
                                onClick={() => setOtpStep('email')}
                            >
                                Change
                            </Button>
                        </div>
                        <div className="fao-otp-input-group">
                            <Input
                                size="large"
                                placeholder="Enter 6-digit OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                onPressEnter={handleVerifyOtp}
                                maxLength={6}
                                className="fao-otp-input"
                            />
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleVerifyOtp}
                                loading={otpVerifying}
                                disabled={otp.length !== 6}
                                className="fao-verify-otp-btn"
                            >
                                Verify
                            </Button>
                        </div>
                        <div className="fao-resend-otp">
                            {otpTimer > 0 ? (
                                <span>Resend OTP in {otpTimer}s</span>
                            ) : (
                                <Button type="link" onClick={handleResendOtp}>
                                    Resend OTP
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );


    const renderStep5 = () => {
        const handleSubmitOnboarding = () => {
            // Save onboarding data
            callAPI({
                url: 'update_onboard_data',
                method: 'PUT',
                body: {
                    previous_tools: onboardingData.previousTools,
                    instagram_use: onboardingData.businessType,
                    main_goal: onboardingData.mainGoal,
                    website: onboardingData.website,
                    whatsapp_number: whatsappNumber
                },
                loadingState: () => { },
                dataState: (response) => {
                    if (response?.code === 200 || response?.code === "200") {
                        message.success('Profile updated!');
                        setCurrentStep(6);
                    } else {
                        message.error(response?.message || 'Failed to save data');
                    }
                }
            });
        };

        return (
            <div className="fao-step fao-step-onboarding">
                <h2 className="fao-title">Tell Us About Yourself</h2>
                <p className="fao-subtitle">
                    Help us personalize your experience
                </p>

                <div className="fao-onboarding-questions">
                    {/* Previous Tools */}
                    <div className="fao-question-section">
                        <h4>Have you used any of these tools before?</h4>
                        <Checkbox.Group
                            style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}
                            value={onboardingData.previousTools}
                            onChange={value => setOnboardingData(prev => ({ ...prev, previousTools: value }))}
                            options={[
                                { label: "Manychat", value: "manychat" },
                                { label: "Linkdm", value: "linkdm" },
                                { label: "Zorcha", value: "zorcha" },
                                { label: "Super Profile", value: "superprofile" },
                                { label: "Inro", value: "inro" },
                                { label: "Reply Rush", value: "replyrush" },
                                { label: "Nothing", value: "none" },
                                { label: "Other", value: "other" }
                            ]}
                        />
                    </div>

                    {/* Business Type */}
                    <div className="fao-question-section">
                        <h4>What suits you best?</h4>
                        <Checkbox.Group
                            style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}
                            value={onboardingData.businessType}
                            onChange={value => setOnboardingData(prev => ({ ...prev, businessType: value }))}
                            options={[
                                { label: "Online Store", value: "online_store" },
                                { label: "Offline Store", value: "offline_store" },
                                { label: "Content Creator", value: "creator" },
                                { label: "Influencer", value: "influencer" },
                                { label: "Coaching / Consulting", value: "coaching" },
                                { label: "Marketing Agency", value: "agency" },
                                { label: "Brand", value: "brand" },
                                { label: "Other", value: "other" }
                            ]}
                        />
                    </div>

                    {/* Main Goal */}
                    <div className="fao-question-section">
                        <h4>What is your main goal with InstantDM?</h4>
                        <Checkbox.Group
                            style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}
                            value={onboardingData.mainGoal}
                            onChange={value => setOnboardingData(prev => ({ ...prev, mainGoal: value }))}
                            options={[
                                { label: "Increase product sales", value: "sales" },
                                { label: "Get more leads / calls", value: "leads" },
                                { label: "Grow my followers", value: "followers" },
                                { label: "Engage with my audience", value: "engagement" }
                            ]}
                        />
                    </div>

                    {/* Website */}
                    <div className="fao-question-section">
                        <h4>Do you have a website? <Tag color="blue">Optional</Tag></h4>
                        <Input
                            prefix={<GlobalOutlined />}
                            value={onboardingData.website}
                            onChange={(e) => setOnboardingData(prev => ({ ...prev, website: e.target.value }))}
                            placeholder='Please share your website link'
                            size="large"
                        />
                    </div>

                    {/* WhatsApp Number */}
                    <div className="fao-question-section">
                        <h4>What's your WhatsApp number? <Tag color="blue">Optional</Tag></h4>
                        <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
                            We'll use this for important updates and support
                        </p>
                        <PhoneInput
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="IN"
                            value={whatsappNumber}
                            onChange={setWhatsappNumber}
                            placeholder="Enter your WhatsApp number"
                            className="phone-input-wrapper"
                        />
                    </div>
                </div>

                <Button
                    type="primary"
                    size="large"
                    className="fao-continue-btn"
                    onClick={handleSubmitOnboarding}
                    disabled={onboardingData.businessType.length === 0 || onboardingData.mainGoal.length === 0}
                >
                    Continue <ArrowRightOutlined />
                </Button>
            </div>
        );
    };

    const renderStep6 = () => (
        <div className="fao-step fao-step-success">
            <div className="fao-success-icon">
                <CheckCircleFilled />
            </div>
            <h2 className="fao-title">You're All Set! 🎉</h2>
            <p className="fao-subtitle">
                Congratulations! Your first automation is working.
                Every time someone comments, they'll automatically receive your DM!
            </p>

            {/* User Profile */}
            <div className="fao-user-profile">
                <img
                    src={decodedToken?.profile_picture_url || '/logo192.png'}
                    alt="Profile"
                    className="fao-user-avatar"
                />
                <span className="fao-user-name">@{decodedToken?.username || 'your_account'}</span>
            </div>

            <div className="fao-final-actions">
                <Button
                    type="primary"
                    size="large"
                    className="fao-setup-btn"
                    onClick={() => {
                        onComplete?.();
                        onSetupAccount?.();
                        onClose?.();
                    }}
                    icon={<RocketOutlined />}
                >
                    Continue to InstantDM
                </Button>
            </div>
        </div>
    );

    const canProceed = () => {
        switch (currentStep) {
            case 0: return true;
            case 1: return selectedPost !== null;
            case 2: return selectedQuickTemplate !== null && (triggerType === 'all' || triggerWords.length > 0);
            default: return true;
        }
    };

    return (
        <Modal
            open={open}
            onCancel={currentStep === 6 ? onClose : undefined}
            closable={currentStep === 6}
            maskClosable={false}
            footer={null}
            width={720}
            className="fao-modal"
            centered
        >
            <div className="fao-container">
                {renderStepIndicator()}

                <div className="fao-content">
                    {currentStep === 0 && renderStep0()}
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}
                    {currentStep === 5 && renderStep5()}
                    {currentStep === 6 && renderStep6()}
                </div>

                {/* Navigation Footer */}
                {currentStep > 0 && currentStep < 3 && (
                    <div className="fao-footer">
                        <Button
                            onClick={handleBack}
                            icon={<ArrowLeftOutlined />}
                        >
                            Back
                        </Button>

                        {currentStep === 2 ? (
                            <Button
                                type="primary"
                                onClick={handleCreateAutomation}
                                loading={creating}
                                disabled={!canProceed()}
                                icon={<ThunderboltOutlined />}
                                className="fao-automate-btn"
                            >
                                {creating ? 'Creating...' : 'Automate This Post'}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                onClick={handleNext}
                                disabled={!canProceed()}
                                icon={<ArrowRightOutlined />}
                            >
                                Next
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default FirstAutomationOnboarding;
